import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import { LEGACY_TAB_FALLBACK, SIDEBAR_DEFAULT_WIDTH, migrateSidebarWidth } from '../types';

/** Layout state managed by LayoutContext. */
export interface LayoutState {
  sidebarOpen: boolean;
  sidebarWidth: number;
  sidebarTab: string;
  quickAccessOpen: boolean;
  toolOptionsVisible: boolean;
  /** GM Studio (поверхность подготовки) открыта вместо стола. */
  studioOpen: boolean;
  /**
   * Мост «⤢ мастерская» (Studio → стол): PageOps живёт в AppShell, из Studio
   * превью включить нельзя — Studio оставляет намерение, AppShell исполняет
   * его на монтировании и сбрасывает. Session-only (санитайзится при загрузке).
   */
  pendingPreviewPageId?: string;
}

/** Actions dispatched to the layout reducer. */
export type LayoutAction =
  | { type: 'layout:toggleSidebar' }
  | { type: 'layout:setSidebarTab'; tab: string }
  | { type: 'layout:setSidebarWidth'; width: number }
  | { type: 'layout:toggleQuickAccess' }
  | { type: 'layout:toggleToolOptions' }
  | { type: 'layout:setToolOptions'; visible: boolean }
  | { type: 'layout:toggleStudio' }
  | { type: 'layout:requestPreview'; pageId: string }
  | { type: 'layout:clearPreviewRequest' };

/** Value provided by LayoutContext. */
export interface LayoutContextValue {
  /** Current layout state */
  state: LayoutState;
  /** Dispatch layout actions */
  dispatch: React.Dispatch<LayoutAction>;
}

/** Default initial layout state. */
export const INITIAL_LAYOUT: LayoutState = {
  sidebarOpen: true,
  sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
  sidebarTab: 'map',
  quickAccessOpen: false,
  toolOptionsVisible: true,
  studioOpen: false,
};

/** Clamp a value between min and max. */
function clampWidth(width: number): number {
  return Math.min(600, Math.max(200, width));
}

/** Reducer for layout state transitions. */
export function layoutReducer(state: LayoutState, action: LayoutAction): LayoutState {
  switch (action.type) {
    case 'layout:toggleSidebar':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    case 'layout:setSidebarTab': {
      const resolvedTab = LEGACY_TAB_FALLBACK[action.tab] ?? action.tab;
      // The same tab is no change: a new object here re-rendered the whole table.
      return resolvedTab === state.sidebarTab ? state : { ...state, sidebarTab: resolvedTab };
    }
    case 'layout:setSidebarWidth':
      return { ...state, sidebarWidth: clampWidth(action.width) };
    case 'layout:toggleQuickAccess':
      return { ...state, quickAccessOpen: !state.quickAccessOpen };
    case 'layout:toggleToolOptions':
      return { ...state, toolOptionsVisible: !state.toolOptionsVisible };
    case 'layout:setToolOptions':
      return { ...state, toolOptionsVisible: action.visible };
    case 'layout:toggleStudio':
      return { ...state, studioOpen: !state.studioOpen };
    case 'layout:requestPreview':
      return { ...state, pendingPreviewPageId: action.pageId };
    case 'layout:clearPreviewRequest':
      return { ...state, pendingPreviewPageId: undefined };
    default:
      return state;
  }
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

/** Apply legacy tab fallback to persisted layout state. */
function applyTabFallback(state: LayoutState): LayoutState {
  const resolved = LEGACY_TAB_FALLBACK[state.sidebarTab];
  if (resolved) {
    return { ...state, sidebarTab: resolved };
  }
  return state;
}

/**
 * Санитайз восстановленного layout-стейта. GM Studio — сессионный режим:
 * персист studioOpen=true заставлял страницу просыпаться сразу в Studio ВМЕСТО
 * AppShell, где живёт вся гидрация данных (sheets/journal/pages) — пустая
 * Библиотека и ощущение «выйти нельзя» после перезагрузки (dev-стенд,
 * 2026-07-24). Загрузка всегда начинается со стола; Studio — только явным
 * действием в живой сессии. Exported for unit tests.
 */
export function sanitizePersistedLayout(persisted: LayoutState): LayoutState {
  return {
    ...persisted,
    sidebarWidth: migrateSidebarWidth(persisted.sidebarWidth),
    studioOpen: false,
    pendingPreviewPageId: undefined,
  };
}

/** Provider for layout state. Place above WorkspaceProvider in the tree. */
export function LayoutProvider({ children }: { children: ReactNode }) {
  const [persisted, setPersisted] = usePersistedState<LayoutState>('layout', INITIAL_LAYOUT);

  const [state, dispatch] = useReducer(
    layoutReducer,
    applyTabFallback(sanitizePersistedLayout(persisted)),
  );

  // Persist state changes
  useEffect(() => {
    setPersisted(state);
  }, [state, setPersisted]);

  // Sync CSS custom properties
  useEffect(() => {
    const style = document.documentElement.style;
    style.setProperty('--sidebar-width', `${state.sidebarWidth}px`);
    // --toolbar-height removed (043-floating-toolbar: horizontal toolbar replaced by floating panel)
    style.setProperty('--sidebar-collapsed', state.sidebarOpen ? '0' : '1');
  }, [state.sidebarWidth, state.sidebarOpen]);

  const value = useMemo<LayoutContextValue>(() => ({
    state,
    dispatch,
  }), [state, dispatch]);

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

/**
 * Access LayoutContext value. Must be used within a LayoutProvider.
 * @throws Error if used outside LayoutProvider.
 */
export function useLayoutContext(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error('useLayoutContext must be used within LayoutProvider');
  return ctx;
}

/**
 * Мягкий вариант для компонентов, которые монтируются и вне LayoutProvider
 * (например, в изолированных тестах): возвращает null вместо исключения.
 */
export function useLayoutContextOptional(): LayoutContextValue | null {
  return useContext(LayoutContext);
}
